import React, { useState, useEffect } from 'react';
import { connect, history } from 'umi';

import { isEmpty } from 'lodash';
import moment from 'moment';
import classnames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Text } from '@digitalc/dxp-ui';
import useGCPI18n from '@/hooks/useGCPI18n';
import { goBack, onHistoryPush, formatDate, formatDateString } from '@/utils/utils';
import Header from '@/components/Header';
import BaseSpin from '@/components/BaseSpin';
import { Clickable } from '@/components';
import hiappLoading from '@/assets/images/hiapp_loading.gif';
import noDataImg from '@/assets/images/memberShipCenter/myRewards/noData.png';
import styles from './index.less';

const TAB_AWARDED = 'G';
const TAB_REDEEMED = 'F';

const MemberMyRewardsNew = props => {
  const { dispatch, rewardList, loading, dxpToken, totalCount, pageIndex, savedActiveTab } = props;
  const GCPI18n = useGCPI18n();

  const { query } = history.location;
  const { notNewPage } = query || {};
  const initialTab = savedActiveTab || TAB_AWARDED;
  const [activeTab, setActiveTab] = useState(initialTab);

  const queryRewardList = async (state = TAB_AWARDED, page = 1) => {
    if (page === 1) {
      await dispatch({
        type: 'memberShipCenter/save',
        payload: {
          rewardHistoryList: [],
          rewardHistoryTotalCount: 0,
          rewardHistoryPageIndex: 1,
        },
      });
    }
    await dispatch({
      type: 'memberShipCenter/queryRewardHistory',
      payload: {
        state,
        pageIndex: page,
        pageSize: 20,
      },
    });
  };

  useEffect(() => {
    queryRewardList(initialTab, 1);
  }, []);

  const handleTabChange = tab => {
    setActiveTab(tab);
    dispatch({ type: 'memberShipCenter/save', payload: { rewardHistoryActiveTab: tab } });
    queryRewardList(tab, 1);
  };

  const loadMoreData = () => {
    const nextPage = pageIndex + 1;
    queryRewardList(activeTab, nextPage);
  };

  const handleGoDetail = reward => {
    onHistoryPush('/memberMyRewardsDetail', {
      rewardInstanceId: reward.rewardInstanceId,
      notNewPage: true,
    });
  };

  const handleGoBack = () => {
    if (notNewPage) {
      history.goBack();
      return;
    }
    goBack();
  };

  const getRewardStatus = item => {
    if (item.state === 'E') {
      return { text: GCPI18n({ id: 'lb_membership_expired' }), type: 'expired' };
    }
    if (item.useState === 'B') {
      return { text: GCPI18n({ id: 'lb_membership_status_redeemed' }), type: 'redeemed' };
    }
    if (
      item.useState === 'A' &&
      item.expDate &&
      moment().isSameOrAfter(moment(formatDateString(item.expDate)))
    ) {
      return { text: GCPI18n({ id: 'lb_membership_expired' }), type: 'expired' };
    }
    return null;
  };

  const renderAwardedCard = item => (
    <Clickable
      key={item.rewardInstanceId}
      className={styles.gridCard}
      onClick={() => handleGoDetail(item)}
    >
      <div className={styles.gridCardImage}>
        {item.imageUrl && <img src={item.imageUrl} alt="" />}
      </div>
      <div className={styles.gridCardName}>{item.rewardName}</div>
    </Clickable>
  );

  const renderRedeemedCard = item => {
    const status = getRewardStatus(item);
    return (
      <Clickable
        key={item.rewardInstanceId}
        className={styles.listItem}
        onClick={() => handleGoDetail(item)}
      >
        {item.achievementDate && (
          <div className={styles.listItemDate}>{formatDate(item.achievementDate)}</div>
        )}
        <div className={styles.listItemName}>{item.rewardName}</div>
        {status && <div className={styles.statusBadge}>{status.text}</div>}
      </Clickable>
    );
  };

  const getEmptyMessage = () => {
    if (activeTab === TAB_AWARDED) {
      return {
        title: GCPI18n({ id: 'lb_membership_no_deals_yet' }),
        tip: GCPI18n({ id: 'tip_membership_deals_shown_here' }),
      };
    }
    return {
      title: GCPI18n({ id: 'lb_membership_no_activity_yet' }),
      tip: GCPI18n({ id: 'tip_membership_start_earning' }),
    };
  };

  const renderEmpty = () => (
    <div className={styles.emptyContainer}>
      <img src={noDataImg} alt="" />
      <div className={styles.emptyTitle}>
        <Text
          style={{
            color: dxpToken.colorTextNeutral,
            marginBottom: `${dxpToken.spacingGapSm / 100}rem`,
          }}
          breakpoint="Mobile"
          size="TitleT23Bold"
        >
          {getEmptyMessage().title}
        </Text>
      </div>
      <div className={styles.emptyTip}>{getEmptyMessage().tip}</div>
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      <Header
        title={GCPI18n({ id: 'lb_membership_my_rewards' })}
        hideClose
        isArrowLeft
        isHiddenBorder
        goBack={handleGoBack}
        titleFontFamily="Lato"
      />
      <BaseSpin spinning={loading} loadingImg={hiappLoading}>
        <div className={styles.tabBar}>
          <Clickable
            className={classnames(styles.tab, { [styles.active]: activeTab === TAB_AWARDED })}
            onClick={() => handleTabChange(TAB_AWARDED)}
          >
            {GCPI18n({ id: 'lb_membership_tab_awarded' })}
            {activeTab === TAB_AWARDED && <div className={styles.tabIndicator} />}
          </Clickable>
          <Clickable
            className={classnames(styles.tab, { [styles.active]: activeTab === TAB_REDEEMED })}
            onClick={() => handleTabChange(TAB_REDEEMED)}
          >
            {GCPI18n({ id: 'lb_membership_tab_redeemed_expired' })}
            {activeTab === TAB_REDEEMED && <div className={styles.tabIndicator} />}
          </Clickable>
        </div>

        <div className={styles.contentContainer} id="memberMyRewardsNewScrollDiv">
          {isEmpty(rewardList) ? (
            renderEmpty()
          ) : (
            <InfiniteScroll
              dataLength={rewardList.length}
              next={loadMoreData}
              hasMore={rewardList.length < totalCount}
              scrollableTarget="memberMyRewardsNewScrollDiv"
              loader={null}
            >
              <div className={activeTab === TAB_AWARDED ? styles.rewardGrid : styles.rewardList}>
                {rewardList.map(item =>
                  activeTab === TAB_AWARDED ? renderAwardedCard(item) : renderRedeemedCard(item),
                )}
              </div>
              {rewardList.length >= totalCount && rewardList.length > 0 && (
                <div className={styles.listEnd}>
                  <span className={styles.line} />
                  <span className={styles.text}>
                    <Text
                      style={{ color: dxpToken.colorTextSubtle }}
                      breakpoint="Mobile"
                      size="ContentC12Regular"
                    >
                      {GCPI18n({ id: 'lb_membership_youre_all_caught_up' })}
                    </Text>
                  </span>
                  <span className={styles.line} />
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </BaseSpin>
    </div>
  );
};

export default connect(({ memberShipCenter, index, loading }) => ({
  rewardList: memberShipCenter.rewardHistoryList,
  totalCount: memberShipCenter.rewardHistoryTotalCount,
  pageIndex: memberShipCenter.rewardHistoryPageIndex,
  savedActiveTab: memberShipCenter.rewardHistoryActiveTab,
  dxpToken: index.dxpToken,
  loading: loading.effects['memberShipCenter/queryRewardHistory'] || false,
}))(MemberMyRewardsNew);
